import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { toastServerError } from "helpers";
import { t } from "i18next";
import { ApiError, ApiServiceError } from "libs/api";
import toast from "react-hot-toast";

import {
  AiAssistant,
  AiLesson,
  AiLessonDraft,
  AiLessonFirePreview,
  AiLessonListResponse,
  EAiLessonStatus
} from "../../aiAgents.types";
import { AI_ASSISTANTS_QUERY_KEY } from "../../constants/enums";
import { AI_LESSON_REPLAY } from "../../constants/lessons";
import { isEndpointMissingError } from "../../helpers/lessons";
import {
  CreateLessonBody,
  DraftLessonBody,
  LessonActionBody,
  ReplayLessonBody,
  SetLessonsEnabledBody,
  SetReplayVerdictBody,
  ToggleLessonBody,
  UpdateLessonBody,
  requestActivateLesson,
  requestArchiveLesson,
  requestCreateLesson,
  requestDraftLesson,
  requestGetLesson,
  requestGetLessons,
  requestLessonFirePreview,
  requestReplayLesson,
  requestRestoreLesson,
  requestSetLessonsEnabled,
  requestSetReplayVerdict,
  requestToggleLesson,
  requestUpdateLesson
} from "./lessons.request";

type LessonsError = ApiServiceError<ApiError>;

// Until the backend serving lessons is deployed, every lessons route answers
// 404. preventDefaultNotFound keeps the global handler from alerting; the
// screens read isEndpointMissingError(error) and hide the feature instead.
// The queries toast their other errors themselves (toastUnlessNotFound), so
// the global bad-request toast is off too, or each error would show twice.
const LESSONS_QUERY_META = {
  preventDefaultNotFound: true,
  preventDefaultBadRequest: true
};

// Create and update show the validator's answer inline (the refused guidance
// words), so the global bad-request toast is turned off for them too.
const LESSONS_FORM_META = {
  preventDefaultNotFound: true,
  preventDefaultBadRequest: true
};

const toastUnlessNotFound = (error: LessonsError) => {
  if (isEndpointMissingError(error)) return;
  toastServerError(error);
};

export const lessonsKey = (assistantId: string) => [
  AI_ASSISTANTS_QUERY_KEY,
  "lessons",
  assistantId
];

// Under the lessons prefix, so every lesson mutation refreshes it too.
export const lessonKey = (assistantId: string, lessonId: string | null) => [
  ...lessonsKey(assistantId),
  "one",
  lessonId
];

// The Lessons tab reads it (useIsMutating) to show "Replay in progress" on
// the row while this browser waits for R1, even once the window is closed.
export const lessonReplayMutationKey = (assistantId: string) => [
  ...lessonsKey(assistantId),
  "replay"
];

interface GetLessonsOptions {
  statuses?: EAiLessonStatus[];
  search?: string;
  page?: number;
  take?: number;
}

export const useGetLessons = (
  assistantId: string | undefined,
  { statuses, search, page = 1, take = 20 }: GetLessonsOptions = {}
) => {
  const query = useQuery<AiLessonListResponse, LessonsError>({
    queryKey: [
      ...lessonsKey(assistantId ?? ""),
      { statuses, search, page, take }
    ],
    queryFn: async () =>
      await requestGetLessons({
        assistantId: assistantId!,
        statuses,
        search: search || undefined,
        page,
        take
      }),
    enabled: Boolean(assistantId),
    keepPreviousData: true,
    meta: LESSONS_QUERY_META,
    onError: toastUnlessNotFound
  });

  return {
    lessons: query.data?.data ?? [],
    meta: query.data?.meta,
    counts: query.data?.counts,
    isUnavailable: isEndpointMissingError(query.error),
    ...query
  };
};

export const useLessonMutations = (assistantId: string) => {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: lessonsKey(assistantId) });

  const createLesson = useMutation<
    AiLesson,
    LessonsError,
    Omit<CreateLessonBody, "assistantId">
  >({
    mutationFn: async (body) =>
      await requestCreateLesson({ ...body, assistantId }),
    meta: LESSONS_FORM_META,
    onSuccess: invalidate
  });

  const updateLesson = useMutation<
    AiLesson,
    LessonsError,
    Omit<UpdateLessonBody, "assistantId">
  >({
    mutationFn: async (body) =>
      await requestUpdateLesson({ ...body, assistantId }),
    meta: LESSONS_FORM_META,
    onSuccess: invalidate
  });

  const toggleLesson = useMutation<
    AiLesson,
    LessonsError,
    Omit<ToggleLessonBody, "assistantId">
  >({
    mutationFn: async (body) =>
      await requestToggleLesson({ ...body, assistantId }),
    meta: LESSONS_FORM_META,
    onError: toastUnlessNotFound,
    onSuccess: invalidate
  });

  const activateLesson = useMutation<
    AiLesson,
    LessonsError,
    Omit<LessonActionBody, "assistantId">
  >({
    mutationFn: async (body) =>
      await requestActivateLesson({ ...body, assistantId }),
    meta: LESSONS_FORM_META,
    onError: toastUnlessNotFound,
    onSuccess: invalidate
  });

  const archiveLesson = useMutation<
    AiLesson,
    LessonsError,
    Omit<LessonActionBody, "assistantId">
  >({
    mutationFn: async (body) =>
      await requestArchiveLesson({ ...body, assistantId }),
    meta: LESSONS_FORM_META,
    onError: toastUnlessNotFound,
    onSuccess: invalidate
  });

  const restoreLesson = useMutation<
    AiLesson,
    LessonsError,
    Omit<LessonActionBody, "assistantId">
  >({
    mutationFn: async (body) =>
      await requestRestoreLesson({ ...body, assistantId }),
    meta: LESSONS_FORM_META,
    onError: toastUnlessNotFound,
    onSuccess: invalidate
  });

  return {
    createLesson,
    updateLesson,
    toggleLesson,
    activateLesson,
    archiveLesson,
    restoreLesson
  };
};

// One paid AI call that proposes two corrected replies, a guidance and tags.
// Nothing is saved. A failure is a 422 the caller turns into a toast.
export const useDraftLesson = (assistantId: string | undefined) =>
  useMutation<
    AiLessonDraft,
    LessonsError,
    Omit<DraftLessonBody, "assistantId">
  >({
    mutationFn: async (body) =>
      await requestDraftLesson({ ...body, assistantId: assistantId! }),
    meta: LESSONS_FORM_META
  });

// L2, polled while a replay runs (poll), so a reload or a second admin sees
// the result land. Errors are left to the caller (parseLessonApiError).
export const useGetLesson = (
  assistantId: string | undefined,
  lessonId: string | null,
  { poll }: { poll: boolean }
) => {
  const query = useQuery<AiLesson, LessonsError>({
    queryKey: lessonKey(assistantId ?? "", lessonId),
    queryFn: async () =>
      await requestGetLesson({
        assistantId: assistantId!,
        lessonId: lessonId!
      }),
    enabled: Boolean(assistantId && lessonId),
    refetchInterval: poll ? AI_LESSON_REPLAY.POLL_MS : false,
    meta: LESSONS_QUERY_META
  });

  return { lesson: query.data, ...query };
};

// The returned lesson goes straight into the "one" key (the open replay
// window shows it at once), then the list and its counts refresh.
const storeLesson = (
  queryClient: QueryClient,
  assistantId: string,
  lesson: AiLesson
) => {
  queryClient.setQueryData(lessonKey(assistantId, lesson.id), lesson);
  return queryClient.invalidateQueries({ queryKey: lessonsKey(assistantId) });
};

// R1. A failure after the server claimed the replay is a 200 whose result
// has status "error", never a 5xx. The end toast lives here rather than in
// the window: a replay can take minutes and the window may be closed by then.
export const useReplayLesson = (assistantId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    AiLesson,
    LessonsError,
    Omit<ReplayLessonBody, "assistantId">
  >({
    mutationKey: lessonReplayMutationKey(assistantId),
    mutationFn: async (body) =>
      await requestReplayLesson({ ...body, assistantId }),
    meta: LESSONS_FORM_META,
    onSuccess: (lesson) => {
      if (lesson.lastReplayResult?.status === "error") {
        toast.error(t("aiLesson.toast.replayFailed"));
      } else {
        toast.success(t("aiLesson.toast.replayDone"));
      }
      return storeLesson(queryClient, assistantId, lesson);
    }
  });
};

// R2: the admin's "Fixed" / "Not fixed".
export const useSetReplayVerdict = (assistantId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    AiLesson,
    LessonsError,
    Omit<SetReplayVerdictBody, "assistantId">
  >({
    mutationFn: async (body) =>
      await requestSetReplayVerdict({ ...body, assistantId }),
    meta: LESSONS_FORM_META,
    onSuccess: (lesson) => storeLesson(queryClient, assistantId, lesson)
  });
};

export const useLessonFirePreview = (
  assistantId: string | undefined,
  lessonId: string | null,
  days = 30
) => {
  const query = useQuery<AiLessonFirePreview, LessonsError>({
    queryKey: [
      ...lessonsKey(assistantId ?? ""),
      "fire-preview",
      lessonId,
      days
    ],
    queryFn: async () =>
      await requestLessonFirePreview({
        assistantId: assistantId!,
        lessonId: lessonId!,
        days
      }),
    enabled: Boolean(assistantId && lessonId),
    meta: LESSONS_QUERY_META,
    onError: toastUnlessNotFound
  });

  return { preview: query.data, ...query };
};

export const useSetLessonsEnabled = (assistantId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    AiAssistant,
    LessonsError,
    Omit<SetLessonsEnabledBody, "assistantId">
  >({
    mutationFn: async (body) =>
      await requestSetLessonsEnabled({ ...body, assistantId }),
    meta: LESSONS_FORM_META,
    onError: toastUnlessNotFound,
    onSuccess: (updated) => {
      const assistantsKey = [AI_ASSISTANTS_QUERY_KEY, "list"];
      // The switch shows the new value at once instead of flipping back
      // until the refetch lands.
      queryClient.setQueryData<{ data: AiAssistant[] }>(assistantsKey, (old) =>
        old
          ? {
              ...old,
              data: old.data.map((assistant) =>
                assistant.id === updated.id
                  ? { ...assistant, lessonsEnabled: updated.lessonsEnabled }
                  : assistant
              )
            }
          : old
      );
      return queryClient.invalidateQueries({ queryKey: assistantsKey });
    }
  });
};
